import React from 'react';

import i18n from 'util/i18n';

import { RELATIONSHIPS, RELATIONSHIPS_STATUS_COHABITANTS } from 'config/formSections/relationships';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { Summary, NameSummary, DateSummary } from 'components/Summary';

import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { divorceAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import {
  FOREIGN_ACTIVITIES_REAL_ESTATE
} from 'constants/sections';
import Cohabitant from './Cohabitant';

const sectionConfig = {
  key: RELATIONSHIPS_STATUS_COHABITANTS.key,
  section: RELATIONSHIPS.name,
  store: RELATIONSHIPS.store,
  subsection: RELATIONSHIPS_STATUS_COHABITANTS.name,
  storeKey: RELATIONSHIPS_STATUS_COHABITANTS.storeKey,
};

export class Cohabitants extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateCohabitantList = this.updateCohabitantList.bind(this);
    this.updateHasCohabitant = this.updateHasCohabitant.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      HasCohabitant: this.props.HasCohabitant,
      CohabitantList: this.props.CohabitantList,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...queue,
    }, [
      FOREIGN_ACTIVITIES_REAL_ESTATE
    ]);
  };

  updateHasCohabitant = (values) => {
    this.update({
      HasCohabitant: values,
      CohabitantList: values.value === 'Yes' ? this.props.CohabitantsList : {},
    });
  };

  updateCohabitantList = (values) => {
    this.update({
      CohabitantList: values,
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const name = NameSummary(o.Name);
    const dates = DateSummary(o.CohabitationBegan);

    return Summary({
      type: i18n.t('relationships.cohabitant.collection.itemType'),
      index,
      left: name,
      right: dates,
      placeholder: i18n.t('identification.othernames.collection.summary.unknown'),
    });
  };

  render() {
    const {
      requireRelationshipMaritalForeignBornDocExpiration, CohabitantList, currentAddress, peopleWhoKnowYou, errors
    } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(CohabitantList.items, peopleWhoKnowYou, divorceAddressNameFields, currentAddress);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return (
      <div
        className="section-content cohabitants"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_STATUS_COHABITANTS.key}
      >
        <h1 className="section-header">{i18n.t('relationships.cohabitant.sectionTitle.title')}</h1>

        <Branch
          name="hasCohabitant"
          label={i18n.t('relationships.cohabitant.heading.hasCohabitant')}
          labelSize="h4"
          className="has-cohabitant"
          {...this.props.HasCohabitant}
          warning
          help="relationships.cohabitant.help.hasCohabitant"
          onUpdate={this.updateHasCohabitant} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          attribute="HasCohabitant"
        />
        <Comments
          title={i18n.t('relationships.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Show when={this.props.HasCohabitant.value === 'Yes'}>
          <Accordion
            {...this.props.CohabitantList}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdate={this.updateCohabitantList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            required={this.props.required}
            description={i18n.t('relationships.cohabitant.collection.description')}
            appendTitle={i18n.t('relationships.cohabitant.collection.appendTitle')}
            appendLabel={i18n.t('relationships.cohabitant.collection.appendLabel')}
            scrollIntoView={this.props.scrollIntoView}
            attribute="CohabitantList"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <Cohabitant
              name="Item"
              spouse={this.props.spouse}
              applicantBirthdate={this.props.applicantBirthdate}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireRelationshipMaritalForeignBornDocExpiration={
                requireRelationshipMaritalForeignBornDocExpiration
              }
              bind
              attribute="CohabitantList"
              formType={this.props.formType}
            />
          </Accordion>
          <Comments
            title={i18n.t('relationships.comments.summary')}
            name="SummaryComments"
            attribute="SummaryComments"
            className="summary-comment"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            onError={this.props.onError}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

Cohabitants.defaultProps = {
  HasCohabitant: {},
  CohabitantList: {},
  Comments: {},
  SummaryComments: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '.bottom-btns',
  scrollIntoView: false,
  requireRelationshipMaritalForeignBornDocExpiration: true,
  errors: [],
};

export default connectSubsection(Cohabitants, sectionConfig);
